/** @jsx isolateComponent */

import { css } from '@emotion/react';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { CARET_SIZE } from './constants';

import {
  Animation,
  Colors,
  Icon,
  Link,
  Typography,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { emitUserInteractionEvent } from '../../utils/avroEvents';

import type {
  ArticleStubType,
  PromoLinksListType,
  UniversalNavLinkType,
} from '../../constants';
import { UNIVERSAL_TOPBAR_HEIGHT } from '../../constants';
import { useUniversalNavContext } from '../../hooks';
import { MegaMenu } from './MegaMenu';

type Props = {
  /**
   * An array of links to display at the bottom of the menu
   */
  bottomLinks?: UniversalNavLinkType[];
  /**
   * Content to render within the link.
   */
  children: ReactNode;
  /**
   * The `href` attribute to apply.
   */
  href: string | null;
  /**
   * Whether or not the link's menu is active
   */
  isMenuOpen?: boolean;
  /**
   * Indicates whether the link is selected.
   */
  isSelected: boolean;
  /**
   * Indicates whether the content displayed in the MegaMenu will be static Product content instead of dynamic data
   */
  isStaticProductTab?: boolean;
  /**
   * The menu that the link is tied to, for accessibility
   */
  ariaControls?: UniversalNavLinkType['text'];
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
  /**
   * Determines spacing styles if link is located
   * in the accounts menu vs. the main menu
   */
  position?: 'navigation' | 'actions-menu';
  /**
   * Accessibility role for the list item, either menuitem or none
   */
  role?: string;
  /**
   * Links to display in the dropdown menu.
   */
  submenu?: Array<UniversalNavLinkType>;
  /**
   * Function to toggle the menu open/closed
   */
  toggleMenu?: () => void;
  /**
   * Article Stubs or Links Lists to display in the menu.
   */
  promoArea?: Array<ArticleStubType | PromoLinksListType>;
};

const Styles = labelStyles('UniversalTopbarLink', {
  root: (isMenuOnly: boolean) =>
    css({
      // caret icons are absolutely positioned (they don't take up width).
      // This space figures in the 24px width of the icon
      // and the 18px space needed between items
      paddingRight: isMenuOnly ? `${CARET_SIZE + 18}px` : 18,
      display: 'flex',
      alignItems: 'center',
      color: Colors.neutral700,
      ...Typography.paragraph3,
      '&:hover, &:focus, &:focus-within': {
        fontWeight: 600,
      },
      '&[data-position="actions-menu"]': {
        paddingRight: 0,
      },
    }),

  link: (isMenuOnly: boolean) =>
    css({
      color: Colors.inherit,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: UNIVERSAL_TOPBAR_HEIGHT,
      lineHeight: `${UNIVERSAL_TOPBAR_HEIGHT}px`,
      ...Typography.base,
      cursor: 'pointer',
      textDecoration: 'none',
      position: 'relative',

      '&:visited': {
        color: 'inherit',
      },

      '&:focus': {
        outline: 'none',
      },
      // Focus backgroundColor defined on `linkInner`
      '&:focus-within > span': {
        '&::before': {
          backgroundColor: Colors.neutral300,
        },
      },
      // Design wants a bold state on hover
      // this can be problematic for content shifting
      // when elements are in a row
      // Approach was recommended here:
      // https://css-tricks.com/bold-on-hover-without-the-layout-shift/
      '&::after': {
        content: 'attr(data-text)',
        height: 0,
        visibility: 'hidden',
        overflow: 'hidden',
        userSelect: 'none',
        pointerEvents: 'none',
        fontWeight: 600,
      },
      // Orange border underneath link that appears on selection
      '&::before': {
        content: '""',
        height: 6,
        width: 0,
        fontWeight: 600,
        position: 'absolute',
        color: Colors.zapier,
        borderBottom: '6px solid',
        bottom: 0,
        left: 0,
        zIndex: 10,
        transition: `width 200ms ${Animation.transitionTimingFunction}`,
      },
      '&[data-selected="true"]': {
        '&::before': {
          width: isMenuOnly ? 'calc(100% + 17px)' : '100%',
        },
      },
    }),
  linkInner: (isMenuOnly: boolean) =>
    // https://stackoverflow.com/questions/55780450/why-pseudo-element-background-color-covered-its-parent-background-color-when-use
    css({
      transformStyle: 'preserve-3d',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 20,
        right: isMenuOnly ? '-28px' : '-10px',
        bottom: 20,
        left: '-10px',
        transform: 'translateZ(-1px)',
        borderRadius: 4,
        height: 32,
      },
    }),
  caret: (isMenuOpen: boolean) => [
    css({
      transition: Animation.transitionValue,
      position: 'absolute',
      top: CARET_SIZE - 1,
      right: `-${CARET_SIZE}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: CARET_SIZE,
      width: CARET_SIZE,
    }),
    isMenuOpen &&
      withModifier('menu-open', {
        transform: 'rotate(-180deg)',
      }),
  ],
});

/**
 * @private
 * Wrapper around `Link` specifically for use within `UniversalTopbar`.
 */
export const UniversalTopbarLink = (props: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();
  //Remove spaces and special characters
  const sanitizedId = props.ariaControls?.replace(/[^\w]/g, '');

  const isMenuOnly = props.href === null;

  const handleEvent = (eventType: string) => {
    let eventMessage: string = '';
    if (eventType === 'click') {
      eventMessage = `${
        props.isMenuOpen ? 'Closing' : 'Opening'
      } UniversalLayout DynamicTopBar: ${
        isMenuOnly ? props.children : props.href
      }`;
    } else {
      eventMessage = `UniversalLayout DynamicTopBar: ${
        isMenuOnly ? props.children : props.href
      }`;
    }
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: eventMessage,
    });
  };

  const LinkComponent = isMenuOnly ? 'button' : Link;
  const link = (
    <LinkComponent
      // @ts-ignore it doesn't matter if `color` is `undefined` on a `span`.
      color={isMenuOnly ? undefined : null}
      css={Styles.link(isMenuOnly)}
      data-text={props.children}
      data-selected={isMenuOnly && props.isMenuOpen}
      href={
        isMenuOnly ? undefined : getFullyQualifiedURL(props.href) || undefined
      }
      onClick={() => {
        handleEvent('click');
        if (props.toggleMenu && !props.href) props.toggleMenu();
      }}
      onMouseEnter={() => handleEvent('hover')}
      aria-controls={sanitizedId}
      aria-expanded={isMenuOnly ? props.isMenuOpen : undefined}
    >
      <span css={Styles.linkInner(isMenuOnly)}>
        {props.children}
        {isMenuOnly && (
          <span css={Styles.caret(props.isMenuOpen)}>
            <Icon isBlock={true} name="arrowSmallDown" size={16} />
          </span>
        )}
      </span>
    </LinkComponent>
  );

  const submenu = props.submenu && (
    <Fragment>
      {link}
      <MegaMenu
        bottomLinks={props.bottomLinks}
        id={sanitizedId || ''}
        isMenuOpen={props.isMenuOpen || false}
        isStaticProductTab={props.isStaticProductTab || false}
        key={(props.isMenuOpen || 'false').toString()}
        layout={props.isStaticProductTab ? 'stretch' : undefined}
        links={props.submenu}
        onCloseMenu={props.onCloseMenu}
        promoArea={props.promoArea}
      />
    </Fragment>
  );

  const WrapperElement = props.position === 'actions-menu' ? 'span' : 'li';

  return (
    <WrapperElement
      css={Styles.root(isMenuOnly)}
      data-position={props.position}
      data-testid="topbar-link"
      role={props.role}
    >
      {submenu || link}
    </WrapperElement>
  );
};

export default UniversalTopbarLink;
